import { useCallback, useMemo } from 'react';
import type { Control, FieldPath, FieldValues, UseControllerProps } from 'react-hook-form';
import { useController } from 'react-hook-form';

import { useMessages } from '@/hooks/messages';

import { DurationField } from '../DurationField';
import FieldErrors from '../FieldErrors';
import { durationFormFieldDefinitions } from './messages';

export type DurationFormFieldProps<
  TFieldValues extends FieldValues,
  TFieldPath extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  onChange?: (value: string | null) => void;
  onBlur?: () => void;
  control: Control<TFieldValues>;
  fieldPath: TFieldPath;
  rules?: UseControllerProps<TFieldValues, TFieldPath>['rules'];
};

export const DurationFormField = <
  TFieldValues extends FieldValues,
  TFieldPath extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  onBlur,
  onChange,
  fieldPath,
  control,
  rules,
}: DurationFormFieldProps<TFieldValues, TFieldPath>) => {
  const messages = useMessages(durationFormFieldDefinitions);

  const composedRules = useMemo<UseControllerProps<TFieldValues, TFieldPath>['rules']>(
    () => ({
      ...rules,
      validate: (value: string) => {
        if (!value || value === '') {
          return true;
        }
        if (/^\d{1,3}\.[0-2][0-9]:[0-5][0-9]$/.test(value)) {
          const hours = Number.parseInt(value.slice(4, 6), 10);
          if (hours < 24) {
            return true;
          }
        }
        return messages.wrongFormat();
      },
    }),
    [rules, messages],
  );

  const {
    field: { name, onBlur: innerOnBlur, onChange: innerOnChange, ref, value },
    fieldState: { invalid, error },
  } = useController({ name: fieldPath, control, rules: composedRules });

  const composedOnBlur = useCallback(() => {
    innerOnBlur();
    if (onBlur) {
      onBlur();
    }
  }, [onBlur, innerOnBlur]);

  const composedOnChange = useCallback(
    (newValue: string | null) => {
      innerOnChange(newValue);
      onChange?.(newValue);
    },
    [onChange, innerOnChange],
  );

  return (
    <div>
      <DurationField
        value={value}
        onChange={composedOnChange}
        ref={ref}
        name={name}
        onBlur={composedOnBlur}
      />
      {invalid && error && <FieldErrors error={error} />}
    </div>
  );
};
